import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { SEAT_PRICE_LABEL, PREMIUM_INCLUDED_SEATS } from '../../utils/seatPricing';
import logo from '../../assets/default-if-logo.png';
import '../../styles/Plans.css';

export default function Plans() {
  const navigate = useNavigate();
  const { auth } = useAuth();

  // Pre-fill from a plan/billing choice carried over from the marketing
  // site's pricing links (stashed into localStorage during signup).
  const storedPlan = (() => {
    try {
      return JSON.parse(localStorage.getItem('signupPlan'));
    } catch {
      return null;
    }
  })();

  const [selected, setSelected] = useState(storedPlan?.plan || null); // 'standard' | 'premium'
  const [billing, setBilling] = useState(storedPlan?.billing || 'monthly'); // 'monthly' | 'annual'

  useEffect(() => {
    if (!auth?.isHydrated) return;
    if (!auth?.isLoggedIn) return;
    if (!selected) return;

    localStorage.setItem(
      'signupPlan',
      JSON.stringify({
        plan: selected,
        billing,
      })
    );
  }, [selected, billing, auth]);

  if (!auth.isHydrated) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  if (!auth.isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  if (auth.onboardingStatus === 'completed') {
    return <Navigate to="/dashboard" replace />;
  }

  const PRICING = {
    standard: {
      monthly: 75,
      annual: 810,
    },
    premium: {
      monthly: 125,
      annual: 1350,
    },
  };

  function choose(plan) {
    setSelected(plan);
  }

  function continueNext() {
    if (!selected) return;
    navigate('/checkout');
  }

  return (
    <div className="plans-scroll">
      <div className="plans-page">
        <div className="plans-logo-row">
          <img src={logo} alt="InteriorFolio" className="plans-logo" />
        </div>

        <header className="plans-header">
          <h1 className="plans-title">Which Plan Best Suits Your Design Firm?</h1>
        </header>

        <section className="plans-billing-toggle">
          <button
            className={billing === 'monthly' ? 'active' : ''}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>

          <button
            className={billing === 'annual' ? 'active' : ''}
            onClick={() => setBilling('annual')}
          >
            Annual (Save 10%)
          </button>
        </section>

        <section className="plans-cards">
          <div
            className={`plan-card ${selected === 'standard' ? 'selected' : ''}`}
            onClick={() => choose('standard')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                choose('standard');
              }
            }}
          >
            <h2>Standard</h2>

            <div className="price">
              {billing === 'monthly' ? (
                <span className="monthly">${PRICING.standard.monthly}/mo</span>
              ) : (
                <span className="annual">${PRICING.standard.annual}/yr</span>
              )}
            </div>

            {billing === 'annual' && (
              <p className="save">Save 10% when you choose an Annual Subscription!</p>
            )}

            <p className="per-user">Per User*</p>

            <p className="description">
              This plan provides essential features for efficient project management and creativity
              in your design firm.
            </p>

            <button
              type="button"
              className="choose-btn"
              onClick={(e) => {
                e.stopPropagation();
                choose('standard');
                navigate('/checkout');
              }}
            >
              Choose Plan
            </button>
          </div>

          <div
            className={`plan-card premium ${selected === 'premium' ? 'selected' : ''}`}
            onClick={() => choose('premium')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                choose('premium');
              }
            }}
          >
            <span className="badge">User&apos;s Favorite</span>

            <h2>Premium</h2>

            <div className="price">
              {billing === 'monthly' ? (
                <span className="monthly">${PRICING.premium.monthly}/mo</span>
              ) : (
                <span className="annual">${PRICING.premium.annual}/yr</span>
              )}
            </div>

            {billing === 'annual' && (
              <p className="save">Save 10% when you choose an Annual Subscription!</p>
            )}

            <p className="per-user">Per User + {PREMIUM_INCLUDED_SEATS} Additional Users*</p>

            <p className="description">
              This plan provides a range of exclusive features, including customizable reporting
              options, linking to QuickBooks Online, and {PREMIUM_INCLUDED_SEATS} free additional
              users for collaboration.
            </p>

            <button
              type="button"
              className="choose-btn"
              onClick={(e) => {
                e.stopPropagation();
                choose('premium');
                navigate('/checkout');
              }}
            >
              Choose Plan
            </button>
          </div>
        </section>

        <p className="plans-footnote">
          *Add more users on either plan for {SEAT_PRICE_LABEL[billing]} each.
        </p>

        <div className="plans-demo-row">
          <button
            type="button"
            className="demo-btn"
            onClick={() => navigate('/demo')}
          >
            Schedule a Demo
          </button>
        </div>

        <section className="plans-compare">
          {[
            ['Proposals, Invoicing and Purchase Orders', true, true],
            ['Standard Reports', true, true],
            ['Time Billing', true, true],
            ['Planner', true, true],
            ['Customizable Reports', false, true],
            [`${PREMIUM_INCLUDED_SEATS} Additional Free Users`, false, true],
            [`Additional Users at ${SEAT_PRICE_LABEL[billing]} Each`, true, true],
            ['QuickBooks Integration', false, true],
          ].map(([label, standard, premium]) => (
            <div key={label} className="compare-row">
              <div className={standard ? 'compare-mark yes' : 'compare-mark no'}>
                {standard ? '\u2713' : '\u2715'}
              </div>
              <div className="label">{label}</div>
              <div className={premium ? 'compare-mark yes' : 'compare-mark no'}>
                {premium ? '\u2713' : '\u2715'}
              </div>
            </div>
          ))}
        </section>

        <div className="plans-footer">
          <button className="continue-btn" disabled={!selected} onClick={continueNext}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
